import { apiDelete, apiGet, apiPatch, apiPost } from './client.js';
import type { WarrantyType } from '@fixflow/shared-types';

export interface WarrantyRule {
  id: string;
  tenantId: string;
  storeId: string | null;
  priority: number;
  serviceId: string | null;
  serviceNamePattern: string | null;
  deviceMakeId: string | null;
  deviceCategory: string | null;
  warrantyType: WarrantyType;
  warrantyDays: number;
  ruleDescription: string | null;
  active: boolean;
  createdAt: string;
  serviceName?: string | null;
  makeName?: string | null;
}

export type WarrantyRuleInput = {
  storeId?: string | null;
  priority?: number;
  serviceId?: string | null;
  serviceNamePattern?: string | null;
  deviceMakeId?: string | null;
  deviceCategory?: string | null;
  warrantyType?: WarrantyType;
  warrantyDays?: number;
  ruleDescription?: string;
  active?: boolean;
};

export function listWarrantyRules(includeInactive = false) {
  return apiGet<WarrantyRule[]>('/api/v1/warranty-rules', {
    params: includeInactive ? { includeInactive: true } : undefined,
  }).then((res) => res.data);
}

export function createWarrantyRule(body: WarrantyRuleInput) {
  return apiPost<WarrantyRule, WarrantyRuleInput>('/api/v1/warranty-rules', body).then(
    (res) => res.data,
  );
}

export function updateWarrantyRule(id: string, body: Partial<WarrantyRuleInput>) {
  return apiPatch<WarrantyRule, Partial<WarrantyRuleInput>>(`/api/v1/warranty-rules/${id}`, body).then(
    (res) => res.data,
  );
}

export function deleteWarrantyRule(id: string) {
  return apiDelete<void>(`/api/v1/warranty-rules/${id}`);
}

export function reorderWarrantyRules(orderedIds: string[]) {
  return apiPost<{ reordered: number }, { orderedIds: string[] }>('/api/v1/warranty-rules/reorder', {
    orderedIds,
  }).then((res) => res.data);
}

export function testWarrantyRule(body: {
  serviceId?: string;
  serviceName?: string;
  makeId?: string;
  deviceCategory?: string;
}) {
  return apiPost<
    {
      matched: boolean;
      warrantyType: WarrantyType;
      warrantyDays: number | null;
      expiresAt: string | null;
      ruleId: string | null;
      ruleDescription: string | null;
      priority: number | null;
      source?: string;
      label?: string;
    },
    typeof body
  >('/api/v1/warranty-rules/test', body).then((res) => res.data);
}

export function applyWarrantyRulesToPricing(dryRun = false) {
  return apiPost<
    {
      dryRun: boolean;
      wouldUpdate: number;
      updated: number;
      preview: Array<{ ruleDescription: string; count: number }>;
    },
    { dryRun?: boolean }
  >('/api/v1/warranty-rules/apply-to-existing-pricing', { dryRun }).then((res) => res.data);
}
